import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { TRIAL_READ_ONLY_MESSAGE, useTrialMode } from "@/hooks/useTrialMode";
import { db } from "@/lib/billing";
import { usePackageAccess, type TemplateRecord } from "@/lib/packageAccess";

const emptyTemplate: Partial<TemplateRecord> = {
  name: "",
  slug: "",
  description: "",
  template_type: "news",
  category: "Actualites",
  target_niche: "",
  language: "fr",
  preview_image_url: "",
  demo_url: "",
  features: [],
  default_sections: [],
  required_package_slug: "news-site-starter",
  required_service_slug: "news-site",
  is_active: true,
};

async function fetchTemplate(id: string): Promise<TemplateRecord | null> {
  const { data, error } = await db.from("site_templates").select("*").eq("id", id).maybeSingle();
  if (error) throw error;
  return data;
}

export default function TemplateFormPage({ mode }: { mode: "create" | "edit" }) {
  const { id } = useParams();
  const access = usePackageAccess();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const trialMode = useTrialMode();
  const [form, setForm] = useState<Partial<TemplateRecord>>(emptyTemplate);
  const { data: existing, isLoading } = useQuery({ queryKey: ["site-template-form", id], queryFn: () => fetchTemplate(id!), enabled: mode === "edit" && !!id });

  useEffect(() => {
    if (existing) setForm(existing);
  }, [existing]);

  const save = useMutation({
    mutationFn: async () => {
      if (trialMode.isReadOnly) throw new Error(TRIAL_READ_ONLY_MESSAGE);
      const payload = {
        ...form,
        slug: form.slug || form.name?.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""),
      };
      const { error } = mode === "edit" && id
        ? await db.from("site_templates").update(payload).eq("id", id)
        : await db.from("site_templates").insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site-templates"] });
      toast.success("Template enregistre");
      navigate("/templates");
    },
    onError: (err: any) => toast.error(err?.message ?? "Impossible d'enregistrer le template"),
  });

  if (access.isLoading || isLoading) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  if (!access.isAdmin) return <Navigate to="/templates" replace />;

  const set = (patch: Partial<TemplateRecord>) => setForm((current) => ({ ...current, ...patch }));
  const setList = (key: "features" | "default_sections", text: string) => set({ [key]: text.split("\n").map((item) => item.trim()).filter(Boolean) } as Partial<TemplateRecord>);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 px-6 py-4 backdrop-blur-xl">
        <h1 className="text-lg font-semibold">{mode === "create" ? "Creer un template" : "Editer le template"}</h1>
      </header>
      <main className="mx-auto max-w-4xl p-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Definition du template</CardTitle></CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <Field label="Nom"><Input value={form.name ?? ""} onChange={(e) => set({ name: e.target.value })} /></Field>
            <Field label="Slug"><Input value={form.slug ?? ""} onChange={(e) => set({ slug: e.target.value })} /></Field>
            <Field label="Description"><Textarea value={form.description ?? ""} onChange={(e) => set({ description: e.target.value })} /></Field>
            <Field label="Type / categorie"><Input value={form.template_type ?? ""} onChange={(e) => set({ template_type: e.target.value })} /></Field>
            <Field label="Niche cible"><Input value={form.target_niche ?? ""} onChange={(e) => set({ target_niche: e.target.value })} /></Field>
            <Field label="Langue"><Input value={form.language ?? "fr"} onChange={(e) => set({ language: e.target.value })} /></Field>
            <Field label="Image preview"><Input value={form.preview_image_url ?? ""} onChange={(e) => set({ preview_image_url: e.target.value })} /></Field>
            <Field label="Demo URL"><Input value={form.demo_url ?? ""} onChange={(e) => set({ demo_url: e.target.value })} /></Field>
            <Field label="Package requis"><Input value={form.required_package_slug ?? ""} onChange={(e) => set({ required_package_slug: e.target.value })} /></Field>
            <Field label="Service requis"><Input value={form.required_service_slug ?? ""} onChange={(e) => set({ required_service_slug: e.target.value })} /></Field>
            <Field label="Fonctionnalites"><Textarea value={(form.features ?? []).join("\n")} onChange={(e) => setList("features", e.target.value)} /></Field>
            <Field label="Sections par defaut"><Textarea value={(form.default_sections ?? []).join("\n")} onChange={(e) => setList("default_sections", e.target.value)} /></Field>
            <div className="flex items-center justify-between rounded-md border border-border p-3"><Label>Actif</Label><Switch checked={Boolean(form.is_active)} onCheckedChange={(is_active) => set({ is_active })} /></div>
            <div className="flex items-center gap-2 md:col-span-2">
              <Button onClick={() => save.mutate()} disabled={save.isPending || trialMode.isReadOnly} title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}>{save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Enregistrer</Button>
              <Button variant="outline" onClick={() => navigate("/templates")}>Annuler</Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-2"><Label>{label}</Label>{children}</div>;
}
